import { useLocalObservable } from "mobx-react";
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthApi } from "../../api-client";
import { createApiConfig } from "../../api/createApiConfig";
import AuthStore from "./authStore";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthStore | null>(null);
AuthContext.displayName = "AuthContext";

/** Routes where we skip silent refresh on startup (public auth pages). */
const PUBLIC_AUTH_ROUTES = ["/login", "/reset-password"];

const isPublicAuthRoute = (pathname: string): boolean => {
  const normalized = pathname.toLowerCase();
  return PUBLIC_AUTH_ROUTES.some(
    (route) => normalized === route || (route !== "/" && normalized.startsWith(route + "/")),
  );
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const location = useLocation();
  const bootstrapRan = useRef(false);

  // Memoize to avoid recreation on every render
  const apiClient = useMemo(() => new AuthApi(createApiConfig()), []);

  // apiClient is passed to constructor; no need to reassign
  const store = useLocalObservable(() => new AuthStore(apiClient));

  // Route-aware bootstrap: skip silent refresh on public auth pages.
  useEffect(() => {
    if (bootstrapRan.current) return;
    bootstrapRan.current = true;

    if (isPublicAuthRoute(location.pathname)) {
      // On public auth routes, skip refresh and mark bootstrap complete immediately.
      console.info("[AUTH] Skipping bootstrap refresh on public auth route:", location.pathname);
      store.setBootstrapping(false);
    } else {
      // On protected routes, attempt silent refresh to restore session.
      store.bootstrapAuth();
    }
  }, []); // Run once on mount

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthStore => {
  const authStore = useContext(AuthContext);

  if (!authStore) {
    throw new Error("AuthProvider not found");
  }

  return authStore;
};
